import { AdminList, AdminDataGrid, GridColumn } from './shared';
/**
 * Audit Log List Component
 * Displays paginated list of admin actions with filtering
 */

import {
  TextField,
  DateField,
  SearchInput,
  SelectInput,
  useRecordContext,
  FunctionField,
} from 'react-admin';
import { Chip, Tooltip, Typography } from '@mui/material';
import { semantic } from '@rekindle/shared/theme';
import { ListActions } from './shared';

/**
 * Action type color mapping
 */
const ACTION_COLORS: Record<string, string> = {
  // User actions
  plan_change: semantic.info,
  bulk_upgrade: semantic.info,
  user_update: semantic.warning,
  // Content actions
  notification_broadcast: semantic.success,
  // Chunk actions
  chunk_regenerate: semantic.warning,
  chunks_regenerate_failed: semantic.warning,
  chunks_regenerate_session: semantic.warning,
  // Default
  default: semantic.neutral,
};

/**
 * Target type labels
 */
const TARGET_TYPE_LABELS: Record<string, string> = {
  user: 'User',
  subscription: 'Subscription',
  notification: 'Notification',
  chunk: 'AI Chunk',
  session: 'Chat Session',
};

/**
 * Custom field to display action with color coding
 */
function ActionField() {
  const record = useRecordContext();
  if (!record?.action) return null;

  const action = record.action.toLowerCase();
  const color = ACTION_COLORS[action] || ACTION_COLORS.default;

  return (
    <Chip
      label={record.action.replace(/_/g, ' ').toUpperCase()}
      size="small"
      sx={{
        backgroundColor: color,
        color: 'white',
        fontWeight: 500,
        fontSize: '0.7rem',
      }}
    />
  );
}

/**
 * Custom field to display target type
 */
function TargetTypeField() {
  const record = useRecordContext();
  if (!record?.targetType) return <Typography variant="body2" color="textSecondary">-</Typography>;

  const label = TARGET_TYPE_LABELS[record.targetType] || record.targetType;

  return (
    <Chip
      label={label}
      size="small"
      variant="outlined"
    />
  );
}

/**
 * Custom field to display details with tooltip
 */
function DetailsField() {
  const record = useRecordContext();
  if (!record?.details) return <Typography variant="body2" color="textSecondary">-</Typography>;

  const detailsStr = typeof record.details === 'string'
    ? record.details
    : JSON.stringify(record.details, null, 2);

  const truncated = detailsStr.length > 50
    ? detailsStr.substring(0, 50) + '...'
    : detailsStr;

  return (
    <Tooltip title={<pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{detailsStr}</pre>}>
      <Typography
        variant="body2"
        sx={{
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: 'help',
        }}
      >
        {truncated}
      </Typography>
    </Tooltip>
  );
}

/**
 * Filters for audit log list
 */
const auditFilters = [
  <SearchInput key="action" source="action" alwaysOn placeholder="Search by action..." />,
  <SelectInput
    key="targetType"
    source="targetType"
    label="Target Type"
    choices={[
      { id: 'user', name: 'User' },
      { id: 'subscription', name: 'Subscription' },
      { id: 'notification', name: 'Notification' },
      { id: 'chunk', name: 'AI Chunk' },
      { id: 'session', name: 'Chat Session' },
    ]}
  />,
];

export function AuditLogList() {
  return (
    <AdminList
      filters={auditFilters}
      actions={<ListActions />}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={50}
    >
      <AdminDataGrid bulkActionButtons={false}>
        <DateField source="createdAt" label="Date" showTime />
        <GridColumn label="Action" source="action"><ActionField /></GridColumn>
        <GridColumn label="Target type" source="targetType"><TargetTypeField /></GridColumn>
        <TextField source="targetId" label="Target ID" />
        <GridColumn label="Details" sortable={false}><DetailsField /></GridColumn>
        <TextField source="adminUserId" label="Admin ID" />
        <FunctionField
          label="IP"
          render={(record: { ipAddress?: string }) =>
            record?.ipAddress ? (
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {record.ipAddress}
              </Typography>
            ) : '-'
          }
        />
      </AdminDataGrid>
    </AdminList>
  );
}
